import React, { useState } from 'react';
import styled from 'styled-components';
import { useLanguage, Language } from '../contexts/LanguageContext';
import { Menu, MenuItem, Button } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const DropdownButton = styled(Button)`
  && {
    background: var(--primary-blue);
    color: #fff;
    padding: 8px 16px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 0.9rem;
    text-transform: uppercase;
    box-shadow: 0 4px 12px rgba(230, 115, 80, 0.2);
    transition: all .3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: var(--font-primary);
    min-width: 80px;

    &:hover {
      background: #f4511e;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(230, 115, 80, 0.3);
    }

    &:active {
      transform: translateY(-1px);
    }

    .MuiButton-endIcon {
      transition: transform 0.3s ease;
      margin-left: 4px;
    }

    &[aria-expanded="true"] .MuiButton-endIcon {
      transform: rotate(180deg);
    }
  }
`;

const StyledMenu = styled(Menu)`
  & .MuiPaper-root {
    border-radius: 12px;
    margin-top: 8px;
    min-width: 100px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
    border: 1px solid var(--border-light);
  }
`;

const StyledMenuItem = styled(MenuItem)<{ $isActive: boolean }>`
  && {
    font-weight: ${props => props.$isActive ? '700' : '500'};
    color: ${props => props.$isActive ? 'var(--primary-orange)' : 'var(--text-dark)'};
    font-family: var(--font-primary);
    font-size: 0.9rem;
    padding: 10px 20px;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(230, 115, 80, 0.05);
      color: var(--primary-blue);
    }
  }
`;

interface LanguageSwitcherProps {
  onLanguageChange?: () => void;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ onLanguageChange }) => {
  const { language, setLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    onLanguageChange?.();
    handleClose();
  };

  const languages: { code: Language; label: string }[] = [
    { code: 'nl', label: 'NL' },
    { code: 'en', label: 'EN' },
    { code: 'tr', label: 'TR' },
  ];

  return (
    <div>
      <DropdownButton
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {language}
      </DropdownButton>
      <StyledMenu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {languages.map((lang) => (
          <StyledMenuItem
            key={lang.code}
            $isActive={language === lang.code}
            onClick={() => handleLanguageChange(lang.code)}
          >
            {lang.label}
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </div>
  );
};

export default LanguageSwitcher;